import React, { useState, useEffect, useCallback, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import { pl } from 'date-fns/locale';
import { MessageCircle, Phone } from 'lucide-react';
import api from '../utils/api';
import BottomNav from '../components/BottomNav';
import PanelToast from '../components/PanelToast';
import RescheduleAppointmentModal from '../components/RescheduleAppointmentModal';
import KpiStrip from '../components/KpiStrip';
import PanelSection from '../components/PanelSection';
import PanelEmptyState from '../components/PanelEmptyState';

function canRescheduleAppointment(appointment) {
  return appointment.status === 'scheduled';
}

function getAppointmentStatusLabel(status) {
  switch (status) {
    case 'completed':
      return 'Odbyta';
    case 'cancelled':
      return 'Odwołana';
    case 'no_show':
      return 'Nie przyszła';
    default:
      return 'Zaplanowana';
  }
}

function isPastAppointment(appointment) {
  return new Date(appointment.scheduled_at).getTime() < Date.now();
}

function normalizePhone(phoneValue) {
  const raw = String(phoneValue || '').trim().replace(/[\s\-()]/g, '');
  if (raw.startsWith('+')) {
    return `+${raw.slice(1).replace(/\D/g, '')}`;
  }
  if (raw.startsWith('00')) {
    return `+${raw.slice(2).replace(/\D/g, '')}`;
  }
  return raw.replace(/\D/g, '');
}

function validatePhone(phoneValue) {
  const digits = normalizePhone(phoneValue).replace(/\D/g, '');
  if (digits.length < 9) {
    return 'Numer telefonu jest za krótki.';
  }
  if (digits.length > 15) {
    return 'Numer telefonu jest za długi.';
  }
  return '';
}

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editNotes, setEditNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const [showAddClient, setShowAddClient] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', phone: '', notes: '' });
  const [newClientErrors, setNewClientErrors] = useState({ name: '', phone: '' });
  const [toast, setToast] = useState(null);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const addClientNameRef = useRef(null);

  const fetchClients = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients${search ? `?search=${encodeURIComponent(search)}` : ''}`);
      setClients(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search]);

  const fetchClientDetails = useCallback(async (client) => {
    const { data } = await api.get(`/clients/${client.id}`);
    setSelected({ ...client, ...data });
    setHistory(data.history || []);
  }, []);

  useEffect(() => {
    const t = setTimeout(fetchClients, 300);
    return () => clearTimeout(t);
  }, [fetchClients]);

  async function handleSelectClient(client) {
    setSelected(client);
    setEditNotes(client.notes || '');
    try {
      await fetchClientDetails(client);
    } catch (err) {
      console.error(err);
    }
  }

  async function handleAddClient(event) {
    event.preventDefault();
    const nextErrors = {
      name: !newClient.name.trim() ? 'Podaj imię klientki.' : '',
      phone: !newClient.phone.trim() ? 'Podaj numer telefonu.' : validatePhone(newClient.phone),
    };
    setNewClientErrors(nextErrors);

    if (nextErrors.name || nextErrors.phone) {
      setToast({ type: 'error', message: 'Popraw zaznaczone pola formularza.' });
      return;
    }

    try {
      await api.post('/clients', {
        ...newClient,
        phone: normalizePhone(newClient.phone),
      });
      setToast({ type: 'success', message: 'Klientka została dodana do bazy.' });
      setNewClient({ name: '', phone: '', notes: '' });
      setNewClientErrors({ name: '', phone: '' });
      setShowAddClient(false);
      fetchClients();
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.error || 'Nie udało się dodać klientki.' });
    }
  }

  const handleSaveNotes = useCallback(async () => {
    if (!selected) return;
    setSavingNotes(true);
    try {
      await api.put(`/clients/${selected.id}`, { notes: editNotes });
      setSelected({ ...selected, notes: editNotes });
      setClients((prev) =>
        prev.map((c) => (c.id === selected.id ? { ...c, notes: editNotes } : c))
      );
      setNotesSaved(true);
    } catch (err) {
      setToast({ type: 'error', message: 'Nie udało się zapisać notatki.' });
    } finally {
      setSavingNotes(false);
    }
  }, [selected, editNotes]);

  useEffect(() => {
    if (!selected) return undefined;

    setNotesSaved(false);
    const timeoutId = setTimeout(() => {
      if (editNotes !== (selected.notes || '')) {
        handleSaveNotes();
      }
    }, 700);

    return () => clearTimeout(timeoutId);
  }, [editNotes, selected, handleSaveNotes]);

  const upcomingHistory = history.filter((item) => canRescheduleAppointment(item) && !isPastAppointment(item));
  const pastHistory = history.filter((item) => !upcomingHistory.includes(item));
  const clientListKpis = [
    { key: 'clients-total', label: 'Klientki', value: clients.length, featured: true },
    { key: 'clients-visible', label: 'Po wyszukiwaniu', value: clients.length, tone: 'neutral' },
    { key: 'clients-notes', label: 'Z notatką', value: clients.filter((client) => client.notes).length, tone: 'soft' },
    {
      key: 'clients-visits',
      label: 'Wizyty łącznie',
      value: clients.reduce((sum, client) => sum + Number(client.total_visits || 0), 0),
      tone: 'accent',
    },
  ];
  const selectedClientKpis = selected ? [
    { key: 'selected-visits', label: 'Wizyty', value: selected.total_visits || 0, featured: true },
    { key: 'selected-earned', label: 'Zarobione', value: Math.round(Number(selected.earned_total || 0)), unit: 'zł', valueClassName: 'kpi-card-value--price', tone: 'accent' },
    { key: 'selected-lost', label: 'Utracone', value: Math.round(Number(selected.lost_total || 0)), unit: 'zł', valueClassName: 'kpi-card-value--price', tone: 'neutral' },
    {
      key: 'selected-last',
      label: 'Ostatnia wizyta',
      value: selected.last_visit ? format(new Date(selected.last_visit), 'd MMM yyyy', { locale: pl }) : 'Brak',
      tone: 'soft',
    },
  ] : [];

  return (
    <div className="panel-page">
      <PanelToast toast={toast} onClose={() => setToast(null)} />

      <div className="panel-header">
        <div className="panel-header-copy">
          <span className="panel-eyebrow">Relacje</span>
          <h1 className="panel-title">Klientki</h1>
          <p className="panel-subtitle">Historia wizyt, notatki i szybki kontakt w jednym widoku.</p>
        </div>
      </div>

      {/* Szczegóły klientki – widok overlay */}
      {selected && (
          <div className="client-detail clients-detail-view">
          <button className="back-btn" onClick={() => setSelected(null)}>
            ← Wróć
          </button>
          <div className="client-detail-header">
            <div className="client-avatar">{selected.name?.[0]?.toUpperCase() || '?'}</div>
            <div>
              <h2 className="client-detail-name">{selected.name}</h2>
              <a href={`tel:${selected.phone}`} className="client-detail-phone">
                {selected.phone}
              </a>
            </div>
            <div className="client-detail-actions">
              <button
                className="icon-btn"
                onClick={() => { window.location.href = `tel:${selected.phone}`; }}
              >
                <Phone size={16} strokeWidth={1.9} />
              </button>
              <button
                className="icon-btn"
                onClick={() => { window.location.href = `sms:${selected.phone}`; }}
              >
                <MessageCircle size={16} strokeWidth={1.9} />
              </button>
            </div>
          </div>

          <KpiStrip
            items={selectedClientKpis}
            variant="stats"
            className="clients-kpi-strip"
            ariaLabel="Podsumowanie klientki"
          />

          <PanelSection className="client-notes-section" title="Notatka">
            <textarea
              className="notes-textarea"
              placeholder="np. alergia na lakier, preferuje manicure krótki..."
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              rows={3}
            />
            <div className="notes-status-row">
              <span className="notes-status-text">
                {savingNotes ? 'Zapisuję notatkę...' : notesSaved ? 'Notatka zapisana' : 'Zmiany zapisują się automatycznie'}
              </span>
              <button
                className="btn-secondary"
                onClick={handleSaveNotes}
                disabled={savingNotes}
              >
                {savingNotes ? 'Zapisuję...' : 'Zapisz teraz'}
              </button>
            </div>
          </PanelSection>

          <PanelSection className="section" title={`Nadchodzące wizyty (${upcomingHistory.length})`}>
            {upcomingHistory.length === 0 && (
              <PanelEmptyState
                title="Brak nadchodzących wizyt"
                description="Kolejne terminy tej klientki pojawią się tutaj automatycznie."
                className="panel-empty-state--compact"
              />
            )}
            {upcomingHistory.map((h) => (
              <div
                key={h.id}
                className={`history-card ${canRescheduleAppointment(h) ? 'history-card--actionable' : ''}`}
                onClick={() => {
                  if (canRescheduleAppointment(h)) {
                    setRescheduleTarget({
                      ...h,
                      client_name: selected.name,
                      client_phone: selected.phone,
                    });
                  }
                }}
                role={canRescheduleAppointment(h) ? 'button' : undefined}
                tabIndex={canRescheduleAppointment(h) ? 0 : undefined}
                onKeyDown={(event) => {
                  if (canRescheduleAppointment(h) && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    setRescheduleTarget({
                      ...h,
                      client_name: selected.name,
                      client_phone: selected.phone,
                    });
                  }
                }}
              >
                <div className="history-date">
                  {format(parseISO(h.scheduled_at), 'd MMMM yyyy, HH:mm', { locale: pl })}
                </div>
                <div className="history-service">{h.service_name}</div>
                <div className="history-meta">
                  {h.price} zł · {h.duration_minutes} min ·{' '}
                  <span className={`status-badge status-${h.status}`}>{getAppointmentStatusLabel(h.status)}</span>
                </div>
                {Array.isArray(h.selected_addons) && h.selected_addons.length > 0 && (
                  <div className="history-notes">Dodatki: {h.selected_addons.map((addon) => addon.name).join(', ')}</div>
                )}
                {canRescheduleAppointment(h) && (
                  <div className="history-hint">Kliknij wizytę, aby zmienić termin.</div>
                )}
                {h.notes && <div className="history-notes">{h.notes}</div>}
              </div>
            ))}
          </PanelSection>

          <PanelSection className="section" title={`Historia wizyt (${pastHistory.length})`}>
            {pastHistory.length === 0 && (
              <PanelEmptyState
                title="Brak historii wizyt"
                description="Po zakończonych wizytach historia tej klientki pojawi się w tym miejscu."
                className="panel-empty-state--compact"
              />
            )}
            {pastHistory.map((h) => (
              <div key={h.id} className="history-card">
                <div className="history-date">
                  {format(parseISO(h.scheduled_at), 'd MMMM yyyy, HH:mm', { locale: pl })}
                </div>
                <div className="history-service">{h.service_name}</div>
                <div className="history-meta">
                  {h.price} zł · {h.duration_minutes} min ·{' '}
                  <span className={`status-badge status-${h.status}`}>{getAppointmentStatusLabel(h.status)}</span>
                </div>
                {Array.isArray(h.selected_addons) && h.selected_addons.length > 0 && (
                  <div className="history-notes">Dodatki: {h.selected_addons.map((addon) => addon.name).join(', ')}</div>
                )}
                {h.notes && <div className="history-notes">{h.notes}</div>}
              </div>
            ))}
          </PanelSection>
        </div>
      )}

      {/* Lista klientek */}
      {!selected && (
        <>
          <div className="clients-page-layout">
            <div className="clients-page-summary">
              <KpiStrip
                items={clientListKpis}
                variant="stats"
                className="clients-kpi-strip"
                ariaLabel="Podsumowanie bazy klientek"
              />
            </div>

            <PanelSection
              className="section client-add-section clients-page-controls"
              title="Baza klientek"
              actions={(
                <button className="quick-btn small" onClick={() => setShowAddClient((prev) => !prev)}>
                  {showAddClient ? 'Zamknij' : 'Dodaj klientkę'}
                </button>
              )}
            >
              {showAddClient && (
                <form className="add-service-form" onSubmit={handleAddClient}>
                  <div className="form-group">
                    <label>Imię klientki</label>
                    <input
                      ref={addClientNameRef}
                      type="text"
                      value={newClient.name}
                      onChange={(event) => {
                        setNewClient((prev) => ({ ...prev, name: event.target.value }));
                        setNewClientErrors((prev) => ({ ...prev, name: '' }));
                      }}
                      placeholder="np. Kasia"
                      className={newClientErrors.name ? 'input-error' : ''}
                      aria-invalid={newClientErrors.name ? 'true' : 'false'}
                    />
                    {newClientErrors.name && <p className="field-error">{newClientErrors.name}</p>}
                  </div>
                  <div className="form-group">
                    <label>Telefon</label>
                    <input
                      type="tel"
                      value={newClient.phone}
                      onChange={(event) => {
                        setNewClient((prev) => ({ ...prev, phone: event.target.value }));
                        setNewClientErrors((prev) => ({ ...prev, phone: '' }));
                      }}
                      placeholder="600 100 200"
                      inputMode="tel"
                      maxLength={20}
                      className={newClientErrors.phone ? 'input-error' : ''}
                      aria-invalid={newClientErrors.phone ? 'true' : 'false'}
                    />
                    {newClientErrors.phone && <p className="field-error">{newClientErrors.phone}</p>}
                  </div>
                  <div className="form-group">
                    <label>Notatka</label>
                    <textarea
                      rows="3"
                      value={newClient.notes}
                      onChange={(event) => setNewClient((prev) => ({ ...prev, notes: event.target.value }))}
                      placeholder="np. preferuje poranne terminy"
                    />
                  </div>
                  <button type="submit" className="btn-primary">Dodaj klientkę</button>
                </form>
              )}

              <div className="search-bar clients-search-bar">
                <input
                  type="search"
                  placeholder="Szukaj po imieniu lub numerze..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </PanelSection>
          </div>

          {loading && <p className="loading-text">Ładowanie...</p>}

          {!loading && clients.length === 0 && (
            <PanelEmptyState
              icon="👤"
              title="Brak klientek"
              description="Baza klientek zacznie się uzupełniać po pierwszych zaakceptowanych lub dodanych wizytach."
              actions={(
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => {
                    setShowAddClient(true);
                    window.setTimeout(() => addClientNameRef.current?.focus(), 0);
                  }}
                >
                  Dodaj pierwszą klientkę
                </button>
              )}
            />
          )}

          <PanelSection className="clients-page-primary" title={`Lista klientek (${clients.length})`}>
            <div className="client-list">
              {clients.map((client) => (
                <button
                  key={client.id}
                  className="client-card"
                  onClick={() => handleSelectClient(client)}
                >
                  <div className="client-avatar">{client.name?.[0]?.toUpperCase() || '?'}</div>
                  <div className="client-info">
                    <div className="client-name">{client.name}</div>
                    <div className="client-meta">
                      {client.phone} · {client.total_visits || 0} wizyt
                      {client.last_visit && (
                        <> · ostatnia: {format(new Date(client.last_visit), 'd MMM yyyy', { locale: pl })}</>
                      )}
                    </div>
                    {client.notes && (
                      <div className="client-notes-preview">{client.notes}</div>
                    )}
                  </div>
                  <span className="client-arrow">›</span>
                </button>
              ))}
            </div>
          </PanelSection>
        </>
      )}

      {rescheduleTarget && selected && (
        <RescheduleAppointmentModal
          appointment={rescheduleTarget}
          onClose={() => setRescheduleTarget(null)}
          onSaved={async () => {
            setRescheduleTarget(null);
            setToast({ type: 'success', message: 'Termin wizyty został zmieniony.' });
            await fetchClientDetails(selected);
            await fetchClients();
          }}
        />
      )}

      <BottomNav active="clients" />
    </div>
  );
}
